import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Paginator} from '../../models/paginator';
import {Task} from '../../models/task';
import {Step} from '../../models/step';
import {Procedure, ProcedureType} from '../../models/procedure';
import {ActivatedRoute, Router} from '@angular/router';
import {ResourceManager} from '../../services/resource-manager';
import {AuthoringManagerService} from '../../services/authoring-manager.service';
import {EquipmentManagerService} from '../../services/equipment-manager.service';
import {Resource, ResourceType, ResourceUtils} from '../../models/resource';
import {UiManagerService} from '../../services/ui-manager.service';
import {TranslatePipe} from '@ngx-translate/core';
import {NewTaskDialogComponent, TaskDialog} from '../../components/dialogs/new-task-dialog/new-task-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {Utils} from '../../utils';
import {MediaGridComponent} from '../../components/media-grid/media-grid.component';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {MatTabGroup} from '@angular/material/tabs';
import {InputDialogData} from '../../components/dialogs/input-dialog/input-dialog.component';
import {Location} from '@angular/common';
import {addSeconds, differenceInSeconds} from 'date-fns';
import {DEBUG, DebugService} from "../../services/debug.service";

enum SortStepBy {
  VIDEO_SEEK_START,
  SEQUENCE
}

@Component({
  selector: 'app-procedure-page',
  templateUrl: './procedure-page.component.html',
  styleUrls: ['./procedure-page.component.scss']
})
export class ProcedurePageComponent implements OnInit, AfterViewInit, OnDestroy {

  isLoading = true;

  // Procedure
  procedure: Procedure;
  selectedStep: Step;
  @ViewChild('contentTabs', {static: false})
  contentTabs: MatTabGroup;

  // Video procedure bind
  @ViewChild('videoSelectionGrid', { static: false })
  videoSelectionGrid: MediaGridComponent;
  isChoosingVideo = false;
  videoLibraryUploading = false;
  resourcePaginator: Paginator<Resource>;
  resources: Resource[];

  // Video playback
  @ViewChild('videoPlaybackHtml', { static: false })
  videoHTML: ElementRef;

  // Task and steps
  videoTime: number = 0;
  videoDuration: number = 0;
  tasks: Task[] = [];
  media: Resource = new Resource();
  recordingTaskRef: Task;
  recordingStartTime: number;
  recordingFinalization: boolean;
  stepDetailsForm: FormGroup;
  isEditingSeeks: boolean;
  seekStartRelativeTime: Date;
  seekEndRelativeTime: Date;

  // Authoring 3D
  canViewAR = false;

  // Step resources
  @ViewChild('stepLibrary', {static: false})
  stepLibrary: MediaGridComponent;
  stepResources: Resource[];
  stepResourcePaginator: Paginator<Resource>;
  isUpdatingStep: boolean;

  // Library resources
  @ViewChild('library', {static: false})
  library: MediaGridComponent;
  libraryResources: Resource[];
  libraryResourcePaginator: Paginator<Resource>;
  _showLibrary: boolean;
  set showLibrary(value: boolean) {
    this._showLibrary = value;
    this.scheduleRealignmentInkBar();
  }
  get showLibrary() {
    return this._showLibrary;
  }

  constructor(
    private resourceManager: ResourceManager,
    private authoringManagerService: AuthoringManagerService,
    private equipmentManagerService: EquipmentManagerService,
    private uiManagerService: UiManagerService,
    private debugService: DebugService,
    private translatePipe: TranslatePipe,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {

    this.route.paramMap.subscribe(async params => {

      const equipmentId = params.get('id');
      const procedureId = params.get('procedureId')

      // Fetch procedure
      this.procedure = await this.authoringManagerService.getProcedure(equipmentId, procedureId);

      // Fetch procedure video
      await this.loadProcedureVideo();

      // Fetch tasks and steps
      this.procedure.tasks = await this.fetchTasksAndSteps();

      // Init step detail form
      this.stepDetailsForm = this.formBuilder.group({
        name: new FormControl('', Validators.required),
        description: new FormControl(''),
        seekStart: new FormControl(0),
        seekEnd: new FormControl(0),
      });

      this.isLoading = false;

    });

  }

  ngAfterViewInit(): void {
    this.uiManagerService.hideSidebar();
  }

  ngOnDestroy(): void {
    this.uiManagerService.showSidebar();
  }

  async fetchTasksAndSteps() {
    return await Promise.all(
      // Get all tasks
      (await this.authoringManagerService.getTasks(this.procedure.id, new Paginator<Task>({ignorePagination: true, orderBy: 'sequence', sorting: 'ASC'}))).map(async task => {
        // Get all steps
        task.steps = await this.authoringManagerService.getSteps(task.id, new Paginator<Step>({ignorePagination: true, orderBy: 'sequence', sorting: 'ASC'}));
        return task;
      })
    )
  }

  async loadProcedureVideo() {
    const media = await this.authoringManagerService.getMainResource(this.procedure).catch(_ => this.debugService.log(DEBUG.AUTHORING, 'No video found for this procedure'));
    this.procedure.videoUrl = media ? (await this.resourceManager.getDownloadSignedUrl(media)).url : null;
  }

  getCardTitle() {
    return `${this.translatePipe.transform('Edit procedure')} ${this.procedure.name}`;
  }

  goBack() {
    this.router.navigate(['../..'], { relativeTo: this.route });
  }

  manageTask(task?: Task) {

    // Skip task editing if is recording a step from video
    if (this.recordingTaskRef) {
      return;
    }

    const dialogRef = this.dialog.open(NewTaskDialogComponent, {
      width: '350px',
      data: {
        taskName: task?.name ?? '',
        isNew: !task,
      } as TaskDialog,
    });

    dialogRef.afterClosed().subscribe(async (result: TaskDialog) => {
      if (result && result.taskName.length > 0) {

        // Remove task if needed
        if (result.remove) {
          if (task) {
            this.authoringManagerService.removeTask(task.id);
            this.procedure.tasks.splice(this.procedure.tasks.findIndex(x => x === task), 1);
          }
          return;
        }

        if (task) {

          // Update task
          task.name = result.taskName;
          await this.authoringManagerService.updateTask(this.procedure, task.id, task);

        } else {

          // Create new task
          const task = new Task();
          task.procedureId = this.procedure.id;
          task.name = result.taskName;
          task.description = '';
          task.sequence = this.procedure.tasks.length;
          task.steps = [];
          task.id = (await this.authoringManagerService.addTask(task)).id;
          this.procedure.tasks.push(task);

        }

      }
    });

  }

  async addStep(parentTask: Task) {

    this.closeStep();

    // If not video -> start editing step
    if (!this.procedure.videoUrl) {

      // Create new step
      const step = new Step();
      // step.task = parentTask;
      step.taskId = parentTask.id;
      step.isNew = true;

      this.manageStep(step);
      return;
    }

    // Otherwise, find seeks

    // Set recording status for parent task
    this.recordingTaskRef = parentTask;
    this.recordingStartTime = this.videoTime;

    // Start video playback
    await this.videoHTML.nativeElement.play();

  }

  async stopStepRecording(parentTask: Task) {

    this.recordingFinalization = true;

    // Start video playback
    await this.videoHTML.nativeElement.pause();

    // Create step with seeks
    const step = new Step();
    // step.task = parentTask;
    step.taskId = parentTask.id;
    step.isNew = true;
    step.seekStart = this.recordingStartTime;
    step.seekEnd = this.videoTime;

    // Finalize step
    this.manageStep(step);

  }

  manageStep(step: Step) {

    // Skip step selection if it is recording a step from video
    if (this.recordingTaskRef && !step.isNew) {
      return;
    }

    // Populate form from model
    this.stepDetailsForm.reset();
    this.stepDetailsForm.setValue({
      name: step.name ?? '',
      description: step.description ?? '',
      seekStart: step.seekStart ?? 0,
      seekEnd: step.seekEnd ?? 0,
    });

    // Select or deselect step
    if (this.selectedStep == step) {
      this.closeStep();
    } else {

      // Select step
      this.selectedStep = step;

      // Enable authoring AR if step is created
      if (!step.isNew) {
        this.enableARView();
      }

      // Set video preview seek to current timestamp if step is created
      if (!step.isNew && this.procedure.videoUrl) {
        this.videoHTML.nativeElement.currentTime = step.seekStart;
      }

      this.scheduleRealignmentInkBar();

    }

  }

  closeStep() {
    this.selectedStep = null;
    this.isEditingSeeks = false;
    this.recordingTaskRef = null;
    this.recordingFinalization = false;
    this.canViewAR = false;
    this.showLibrary = false;
    this.contentTabs.selectedIndex = 0;
    this.scheduleRealignmentInkBar();
  }

  scheduleRealignmentInkBar() {
    setTimeout(() => {
      this.contentTabs.realignInkBar();
    }, 250);
  }

  async removeStep(step: Step) {

    // Remove step locally
    const task = this.procedure.tasks.find(task => task.id == (step.taskId ?? step.task.id));
    task.steps.splice(task.steps.findIndex(x => x === step), 1);

    // Close step details
    this.closeStep();

    // Remove step from backend
    await this.authoringManagerService.removeStep(step.id);

    // Order steps
    await this.orderSteps(step.task.id, step.hasSeeks() ? SortStepBy.VIDEO_SEEK_START : SortStepBy.SEQUENCE);

  }

  async createOrUpdateStep(step: Step) {

    // Populate step model from form
    Object.assign(step, this.stepDetailsForm.value);

    // Reset flags
    this.recordingFinalization = false;
    this.recordingTaskRef = null;

    if (step.isNew) {

      // Create new step

      step.id = (await this.authoringManagerService.addStep(step)).id;

      // Array update not handled by drag and drop module

      // Add to timeline
      const task = this.procedure.tasks.find(task => task.id == (step.taskId ?? step.task.id));
      task.steps.push(step);

      // Order steps
      await this.orderSteps(task.id, step.hasSeeks() ? SortStepBy.VIDEO_SEEK_START : SortStepBy.SEQUENCE);

      // Enable AR for this step
      this.enableARView();

      step.isNew = false;

    } else {

      // Update step
      await this.authoringManagerService.updateStep(step.id, step);

    }

  }

  chooseVideo() {

    this.isChoosingVideo = true;

    // Init video loader
    this.resourcePaginator = new Paginator<Resource>({ pageSize: 9 });
    this.resourcePaginator.onPageResults = (resources: Resource[]) => {
      this.resources = resources;
    }
    this.resourceManager.getResources(this.procedure.equipmentModel.id, this.resourcePaginator);

  }

  disposeVideoGrid() {
    this.isChoosingVideo = false;
    this.resources = [];
    this.resourcePaginator = null;
  }

  onVideoSelected(resource: Resource) {

    this.uiManagerService.showConfirmDialog('Choose resource', 'Choose selected resource as video for this procedure?', async () => {

      // Bind video resource to procedure
      await this.authoringManagerService.bindResourceToProcedure(this.procedure, resource);

      // Dispose video selection grid
      this.disposeVideoGrid();

      // Load procedure video
      await this.loadProcedureVideo();

    }, () => {
      this.videoSelectionGrid.clearSelection();
    });

  }

  unlinkVideo() {

    this.uiManagerService.showConfirmDialog('Unlink video', 'Are you sure do you want to unlink this video from this procedure?', async () => {

      // Get resource for current procedure
      const resource = await this.authoringManagerService.getMainResource(this.procedure).catch(_ => this.debugService.log(DEBUG.AUTHORING, 'No video found for this procedure'));
      if (!resource) {
        return;
      }

      // Clear video
      this.procedure.videoUrl = null;

      // Unlink video from procedure
      await this.authoringManagerService.unbindResourceFromProcedure(this.procedure, resource);

    })

  }

  async addResourcesToStep() {

    this.contentTabs.selectedIndex = 1;

    this.isUpdatingStep = true;

    const resourceIds = this.library.selectedResources;

    this.library.clearSelection();

    await this.resourceManager.addStepResources(this.selectedStep.id, resourceIds);

    await this.stepResourcePaginator.refresh();

    this.isUpdatingStep = false;

  }

  async removeResourcesFromStep() {

    this.isUpdatingStep = true;

    const resourceIds = this.stepLibrary.selectedResources;

    this.stepLibrary.clearSelection();

    await this.resourceManager.removeStepResources(this.selectedStep.id, resourceIds);

    await this.stepResourcePaginator.refresh();

    this.isUpdatingStep = false;

  }

  getStepFromTime(step: Step) {
    return Utils.getTime(Utils.getTime2Hours(step.seekStart), Utils.getTime2Minutes(step.seekStart), Utils.getTime2Seconds(step.seekStart));
  }

  getStepToTime(step: Step) {
    return Utils.getTime(Utils.getTime2Hours(step.seekEnd), Utils.getTime2Minutes(step.seekEnd), Utils.getTime2Seconds(step.seekEnd));
  }

  drop(event: CdkDragDrop<Step[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.orderSteps(event.item.data.taskId || event.item.data.task.id, SortStepBy.SEQUENCE);
    } /*else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
    }*/
  }

  async orderSteps(taskId: string, sortBy: SortStepBy) {

    // Sort by video seek start or by sequence
    /*if (sortBy == SortStepBy.VIDEO_SEEK_START) {
      task.steps = task.steps.sort((a, b) => (a.seekStart > b.seekStart) ? 1 : ((b.seekStart > a.seekStart) ? -1 : 0));
    } else {
      task.steps = task.steps.sort((a, b) => (a.sequence > b.sequence) ? 1 : ((b.sequence > a.sequence) ? -1 : 0));
    }*/

    const task = this.procedure.tasks.find(x => x.id == taskId);

    // Update local sequence numbers
    task.steps.forEach((step, index) => task.steps[index].sequence = index);

    // Push new sequence
    await this.authoringManagerService.updateStepSequence(task.id, task.steps);

  }

  onVideoTimeUpdate(time: number) {
    this.videoTime = time;
  }

  getVideoTime2Seconds() {
    return Utils.getTime2Seconds(this.videoTime);
  }
  getVideoTime2Minutes() {
    return Utils.getTime2Minutes(this.videoTime);
  }
  getVideoTime2Hours() {
    return Utils.getTime2Hours(this.videoTime);
  }

  videoDeltaTime(): number {
    return Math.round(this.videoTime - this.recordingStartTime);
  }

  getStepDuration(): number {
    return Math.round(this.stepDetailsForm.value.seekEnd - this.stepDetailsForm.value.seekStart);
  }

  async enableARView() {

    this.canViewAR = true;
    await this.initLibraries();
    this.scheduleRealignmentInkBar();

  }

  async initLibraries() {

    this.showLibrary = true;
    this.isUpdatingStep = false;

    // Init step library
    this.stepResourcePaginator = new Paginator<Resource>({ pageSize: 9 });
    this.stepResourcePaginator.onPageResults = (resources: Resource[]) => {
      this.stepResources = resources;
    }
    const stepResourcesPromise = this.resourceManager.getStepResources(this.selectedStep.id, this.stepResourcePaginator);
    // await this.resourceManager.getResources(this.procedure.equipmentModel.id, this.resourcePaginator);

    // Init global library if required
    let libraryResourcesPromise;
    if (!this.libraryResourcePaginator) {
      this.libraryResourcePaginator = new Paginator<Resource>({ pageSize: 9 });
      this.libraryResourcePaginator.onPageResults = (resources: Resource[]) => {
        this.libraryResources = resources;
      }
      libraryResourcesPromise = this.resourceManager.getResources(this.procedure.equipmentModel.id, this.libraryResourcePaginator);
    }

    await Promise.all([
      stepResourcesPromise,
      libraryResourcesPromise
    ]);

  }

  updateProcedure() {
    this.uiManagerService.showInputDialog(
      'Update procedure',
      'Update',
      { input: { name: this.procedure.name, description: this.procedure.description } }, async (result: InputDialogData) => {

        // Update procedure object
        this.procedure.modelId = this.procedure.equipmentModel.id;
        this.procedure.name = result.input.name;
        this.procedure.description = result.input.description;

        // Update procedure
        const updatedProcedure = await this.authoringManagerService.updateProcedure(this.procedure.id, this.procedure);
        this.procedure.name = updatedProcedure.name;
        this.procedure.description = updatedProcedure.description;

      });
  }

  async saveAndClose() {

    this.isLoading = true;

    this.procedure.types = await this.authoringManagerService.inferProcedureTypes(this.procedure);

    await this.authoringManagerService.updateProcedure(this.procedure.id, this.procedure);

    this.location.back();

    // TODO export maintenance to external API
    // await this.authoringManagerService.exportProcedureToExternalService(this.accountManagerService.companyId, this.equipmentId, this.procedureId);
  }

  editSeeks() {
    this.isEditingSeeks = true;
    this.updateSeeks();
  }

  updateSeeks() {
    this.seekStartRelativeTime = addSeconds(Utils.getZeroDate(), this.stepDetailsForm.value.seekStart);
    this.seekEndRelativeTime = addSeconds(Utils.getZeroDate(), this.stepDetailsForm.value.seekEnd);
  }

  onUpdateStartSeek() {
    let seekStart = differenceInSeconds(this.seekStartRelativeTime, Utils.getZeroDate());

    // Over video seek
    if (seekStart > this.videoDuration) {
      seekStart = this.videoDuration;
      this.updateSeeks();
    }

    // Remove time inconsistency (negative delta times)
    this.stepDetailsForm.controls.seekStart.setValue(seekStart);
    if (this.stepDetailsForm.value.seekEnd < seekStart) {
      this.stepDetailsForm.controls.seekEnd.setValue(seekStart);
      this.updateSeeks();
    }
  }

  onUpdateEndSeek() {
    let seekEnd = differenceInSeconds(this.seekEndRelativeTime, Utils.getZeroDate());

    // Over video seek
    if (seekEnd > this.videoDuration) {
      seekEnd = this.videoDuration;
      this.updateSeeks();
    }

    // Remove time inconsistency (negative delta times)
    this.stepDetailsForm.controls.seekEnd.setValue(seekEnd);
    if (seekEnd < this.stepDetailsForm.value.seekStart) {
      this.stepDetailsForm.controls.seekStart.setValue(seekEnd);
      this.updateSeeks();
    }
  }

  get Utils() { return Utils; }

  get ResourceType() { return ResourceType; }

  get ResourceUtils() { return ResourceUtils; }

}
